import React, { useEffect } from "react";
import MaterialTable from 'material-table';
import { useForm } from 'react-hook-form';
import Select from 'react-select'
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

// reactstrap components
import {
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Form,
  Row,
  Col,
  Input,
  Button,
  FormGroup
} from "reactstrap";

// style
import { table_translate, table_style } from "constants/MaterialTable.js";

// services
import { showMessage, showQuestion } from "../../services/message.js";
import { api_GET, api_POST, api_PUT } from "../../services/api_service.js";
import ValidaAcessoFuncionalidadeAcao from "../../services/acesso.js";

import { useAuth } from "context/auth.js";
import { textoCertidaoParaEditor, textoCertidaoParaGravar, variaveisQuebradas } from "helpers/textoCertidao.js";

// A certidão é impressa fora do editor, onde o CSS do Quill não existe: alinhamento,
// fonte e tamanho precisam sair como estilo inline, não como classe ql-*.
const AlignStyle = Quill.import("attributors/style/align");
const SizeStyle = Quill.import("attributors/style/size");
const FontStyle = Quill.import("attributors/style/font");

const TAMANHOS_FONTE = ["10px", "12px", "14px", "16px", "18px", "24px", "32px"];
const FONTES = ["Arial", "Calibri", "Times New Roman", "Verdana"];

// A whitelist do Quill é global ao bundle: serif/monospace continuam aceitos porque são
// os valores do picker padrão (`font: []`) usado pelos outros editores do sistema.
SizeStyle.whitelist = TAMANHOS_FONTE;
FontStyle.whitelist = [...FONTES, "serif", "monospace"];
Quill.register(AlignStyle, true);
Quill.register(SizeStyle, true);
Quill.register(FontStyle, true);

// Sem imagem e link: o cabeçalho/logo já vem do layout da certidão, e imagem colada
// entraria em base64 dentro do texto do modelo.
const quill_modules = {
  toolbar: [
    [{ font: [false, ...FONTES] }, { size: [false, ...TAMANHOS_FONTE] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["clean"],
  ],
};

const quill_formats = [
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "list",
  "bullet",
  "indent",
];

// O picker do Quill só tem rótulo para os valores padrão dele; fontes e tamanhos
// próprios precisam do texto definido aqui.
const estilo_editor = [
  ".modelo-certidao-editor .ql-container { min-height: 600px; font-size: 14px; }",
  ".modelo-certidao-editor .ql-editor { min-height: 600px; font-family: inherit; }",
  ...TAMANHOS_FONTE.map((t) =>
    `.modelo-certidao-editor .ql-picker.ql-size .ql-picker-label[data-value="${t}"]::before,
     .modelo-certidao-editor .ql-picker.ql-size .ql-picker-item[data-value="${t}"]::before { content: "${t}"; }`
  ),
  ".modelo-certidao-editor .ql-picker.ql-size .ql-picker-label:not([data-value])::before, .modelo-certidao-editor .ql-picker.ql-size .ql-picker-item:not([data-value])::before { content: \"Padrão\"; }",
  ...FONTES.map((f) =>
    `.modelo-certidao-editor .ql-picker.ql-font .ql-picker-label[data-value="${f}"]::before,
     .modelo-certidao-editor .ql-picker.ql-font .ql-picker-item[data-value="${f}"]::before { content: "${f}"; font-family: "${f}"; }`
  ),
  ".modelo-certidao-editor .ql-picker.ql-font .ql-picker-label:not([data-value])::before, .modelo-certidao-editor .ql-picker.ql-font .ql-picker-item:not([data-value])::before { content: \"Padrão\"; }",
  ".modelo-certidao-editor .ql-picker.ql-font { width: 140px; }",
].join("\n");

export default function CadastroModeloCertidao({ history }) {
  const { session, setLoading } = useAuth();

  const { register, handleSubmit, errors, setValue, clearErrors } = useForm({ mode: "onBlur" });

  const quillRef = React.useRef(null);
  const [textoCertidao, set_textoCertidao] = React.useState("");

  const [permiteEditar, set_permiteEditar] = React.useState(false);

  const [status, set_status] = React.useState("");
  const [options_status] = React.useState([
    { value: "Ativo", label: "Ativo" },
    { value: "Inativo", label: "Inativo" },
  ]);
  const handleStatus = (value) => {
    set_status(value);
    setValue("status", value, true);
    clearErrors("status");
  }

  const [rowEdit, set_rowEdit] = React.useState(null);
  const [openCollapse, set_openCollapse] = React.useState(false);
  const [list_table, set_list_table] = React.useState([]);
  const [list_variaveis, set_list_variaveis] = React.useState([]);

  const loadList = () => {
    set_openCollapse(false);
    set_rowEdit(null);
    setLoading(true);
    api_GET('ModeloCertidao')
      .then(result => set_list_table(result.data))
      .catch(err => showMessage("Aviso", err.message, 'error', null))
      .finally(() => setLoading(false));
  }

  const loadVariaveis = () => {
    api_GET('ModeloCertidao/GetVariaveis')
      .then(result => set_list_variaveis(result.data))
      .catch(err => showMessage("Aviso", err.message, 'error', null));
  }

  const handleAdd = () => {
    set_rowEdit(null);
    setValue("descricao", "");
    setValue("sigla", "");
    set_textoCertidao("");
    setValue("status", "", false);
    set_status("");
    set_openCollapse(true);
  }

  const handleEdit = (rowData) => {
    set_rowEdit(rowData);
    setValue("descricao", rowData.descricao);
    setValue("sigla", rowData.sigla);
    set_textoCertidao(textoCertidaoParaEditor(rowData.textoCertidao));
    handleStatus(rowData.status);
    setValue("status", rowData.status, false);
    set_openCollapse(true);
  }

  const handleDelete = (rowData) => {
    showQuestion("Aviso", "Tem certeza que quer deletar o registro?", 'question', function (retorno) {
      if (retorno) {
        setLoading(true);
        api_POST('ModeloCertidao/Delete', rowData)
          .then(result => showMessage("Aviso", "Operação executada com sucesso!", 'success', function () { loadList() }))
          .catch(err => showMessage("Aviso", err.message, 'error', null))
          .finally(() => setLoading(false));
      }
    });
  }

  // Insere o placeholder da variável na posição do cursor (ou no final, se não houver foco)
  const handleInsereVariavel = (variavel) => {
    const editor = quillRef.current && quillRef.current.getEditor();

    if (!editor) return;

    const selecao = editor.getSelection(true);
    const posicao = selecao ? selecao.index : editor.getLength() - 1;

    editor.insertText(posicao, variavel.placeholder, "user");
    editor.setSelection(posicao + variavel.placeholder.length, 0);
  }

  const onSubmit = (data) => {

    const quebradas = variaveisQuebradas(textoCertidao, list_variaveis);

    if (quebradas.length > 0) {
      showMessage(
        "Atenção",
        "As variáveis abaixo estão com formatação aplicada em apenas parte do texto e não serão substituídas na certidão: " +
        quebradas.map((v) => v.descricao).join(", ") +
        ". Apague e insira novamente pelo botão da variável, ou aplique a formatação na variável inteira.",
        "warning",
        null
      );
      return;
    }

    let json_submit = {
      modeloCertidao_Id: rowEdit != null ? rowEdit.modeloCertidao_Id : 0,
      descricao: data.descricao,
      sigla: data.sigla,
      status: status,
      textoCertidao: textoCertidaoParaGravar(textoCertidao),
      usuarioCadastro_Id: session.usuario_Id,
      usuarioUltimaAlteracao_Id: session.usuario_Id
    }

    setLoading(true);
    if (json_submit.modeloCertidao_Id == 0) {
      api_POST('ModeloCertidao', json_submit)
        .then(result => showMessage("Aviso", "Operação executada com sucesso!", 'success', function () { loadList() }))
        .catch(err => showMessage("Aviso", err.message, 'error', null))
        .finally(() => setLoading(false));
    }
    else {
      api_PUT('ModeloCertidao', json_submit)
        .then(result => showMessage("Aviso", "Operação executada com sucesso!", 'success', function () { loadList() }))
        .catch(err => showMessage("Aviso", err.message, 'error', null))
        .finally(() => setLoading(false));
    }
  }

  useEffect(() => {
    ValidaAcessoFuncionalidadeAcao("ModeloCertidao.Editar", function (result) { set_permiteEditar(result) });
    loadVariaveis();
    loadList();
  }, []);

  return (
    <>
      <div className="content">
        <button type="button" disabled={!permiteEditar} onClick={handleAdd} className="btn btn-secundary"><i className="fa fa-plus"></i>  Novo Modelo de Certidão</button>
        <Collapse isOpen={openCollapse} defaultOpen={false}>
          <Row>
            <Col md="12">
              <Card className="card-user">
                <CardHeader>
                  <Row><Col md="12" className="text-right"><label className="text-danger">* Campos Obrigatórios</label></Col></Row>
                </CardHeader>
                <CardBody style={{ minHeight: "auto" }}>
                  <Form style={{ maxWidth: "95%", margin: "0 auto" }} onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                      <Col md="8" sm="12">
                        <FormGroup>
                          <b><label><span className="text-danger">*</span> Descrição</label></b>
                          <Input type="text" name="descricao" maxLength={100} innerRef={register({ required: true })} />
                          {errors.descricao && <span className="form-control-label text-danger">* Campo Obrigatório</span>}
                        </FormGroup>
                      </Col>
                      <Col md="2" sm="6">
                        <FormGroup>
                          <b><label><span className="text-danger">*</span> Sigla</label></b>
                          <Input type="text" name="sigla" maxLength={10} innerRef={register({ required: true })} />
                          {errors.sigla && <span className="form-control-label text-danger">* Campo Obrigatório</span>}
                        </FormGroup>
                      </Col>
                      <Col md="2" sm="6">
                        <FormGroup>
                          <b><label><span className="text-danger">*</span> Status</label></b>
                          <Select
                            options={options_status}
                            name="status"
                            getOptionLabel={({ label }) => label}
                            getOptionValue={({ value }) => value}
                            onChange={(e) => { handleStatus(e.value) }}
                            value={status != "" && options_status.find(obj => obj.value === status)}
                            placeholder="Selecione"
                            ref={e => register(
                              { name: "status" },
                              { required: true })
                            }
                          />
                          {errors.status && <span className="form-control-label text-danger">* Campo Obrigatório</span>}
                        </FormGroup>
                      </Col>
                      <Col md="12" sm="12">
                        <FormGroup>
                          <b><label> Variáveis disponíveis</label></b>
                          <div>
                            <small className="text-muted">Clique na variável para inserir no texto da certidão.</small>
                          </div>
                          <div style={{ marginTop: "8px" }}>
                            {list_variaveis.map((variavel) => (
                              <Button
                                key={variavel.chave}
                                type="button"
                                color="info"
                                size="sm"
                                style={{ marginRight: "6px", marginBottom: "6px" }}
                                onClick={() => handleInsereVariavel(variavel)}
                              >
                                {variavel.descricao}
                              </Button>
                            ))}
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="12" sm="12">
                        <FormGroup>
                          <b><label> Texto da Certidão</label></b>
                          <style>{estilo_editor}</style>
                          <div className="modelo-certidao-editor">
                            <ReactQuill
                              ref={quillRef}
                              theme="snow"
                              value={textoCertidao}
                              onChange={set_textoCertidao}
                              modules={quill_modules}
                              formats={quill_formats}
                              readOnly={!permiteEditar}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                      <Col className="text-center" md="12" sm="12">
                        <FormGroup>
                          <Button disabled={!permiteEditar} color="success" type="submit"><i className="fas fa-save"></i>  Salvar</Button>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Collapse>
        <Row>
          <Col md="12">
            <MaterialTable style={{ minHeight: "auto" }}
              title=""
              columns={[
                { title: 'Descrição', field: 'descricao' },
                { title: 'Sigla', field: 'sigla' },
                { title: 'Status', field: 'status' },
              ]}
              data={list_table}
              isLoading={false}
              options={{
                ...table_style,
                filtering: false,
                exportButton: true
              }}
              actions={[
                {
                  icon: 'edit',
                  tooltip: 'Editar Registro',
                  onClick: (event, rowData) => handleEdit(rowData)
                },
                {
                  icon: 'delete',
                  tooltip: 'Excluir Registro',
                  onClick: (event, rowData) => handleDelete(rowData)
                }
              ]}
              localization={table_translate}
            />
          </Col>
        </Row>
      </div>
    </>
  );

}
